/**
 * =============================================================================
 * CommissionPayoutsModal — Drill-down de Pagamentos de uma Comissão (Indicação)
 *
 * Por que existe:
 *   Uma comissão (`affiliate_commissions`) pode aparecer em N pagamentos
 *   ao longo do tempo via `affiliate_payout_commission_links` (relação N:N
 *   com `released_at` indicando histórico). O ponto único onde isso é
 *   visualizado é este modal — espelho lógico do `PayoutCommissionsModal`,
 *   mas pivotando do lado da comissão.
 *
 *   Mostra:
 *     - Cada pagamento vinculado (link ATIVO + histórico de liberados)
 *     - Status, método e valor de cada link
 *     - Datas de vínculo e liberação
 *
 *   Acesso:
 *     - Aberto a partir do botão pill "N pagamentos →" da
 *       `CommissionsTableView`.
 *     - Permite drill-down adicional ao `PayoutDetailSheet` clicando na
 *       referência do pagamento.
 *
 * @version 1.0.0
 * RELEASE:
 *   2026-04-30 v1.0.0 - Criação. Espelha PayoutCommissionsModal.
 * =============================================================================
 */
import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  HandCoins,
  Wallet,
  ExternalLink,
  CircleDollarSign,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { StatusBar } from '@/components/referral/shared/StatusBar';
import {
  useCommissionPayoutsDetail,
  type CommissionPayoutDetail,
} from '@/hooks/useCommissionPayoutsDetail';

// ============================================================================
// CONSTANTES (paridade visual com PayoutCommissionsModal / PayoutsTableView)
// ============================================================================

/** Mapeamento de status do pagamento → label e cor do badge. */
const PAYOUT_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  pending:    { label: 'Pendente',    color: 'bg-yellow-500 text-white border-yellow-500' },
  processing: { label: 'Processando', color: 'bg-blue-500 text-white border-blue-500' },
  paid:       { label: 'Pago',        color: 'bg-green-500 text-white border-green-500' },
  failed:     { label: 'Falhou',      color: 'bg-red-500 text-white border-red-500' },
  cancelled:  { label: 'Cancelado',   color: 'bg-muted-foreground text-white border-muted-foreground' },
};

/** Cor da barra lateral por status do pagamento (mesma paleta). */
function getPayoutStatusBarColor(status: string): string {
  switch (status) {
    case 'paid':       return 'bg-green-500';
    case 'processing': return 'bg-blue-500';
    case 'pending':    return 'bg-yellow-500';
    case 'failed':     return 'bg-red-500';
    case 'cancelled':  return 'bg-muted-foreground/40';
    default:           return 'bg-muted-foreground/40';
  }
}

// ============================================================================
// UTILITÁRIOS
// ============================================================================

/** Formata valor monetário em BRL. */
function formatBRL(value: number | null | undefined): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value ?? 0));
}

/** Formata data segura (ou retorna '—'). */
function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return format(new Date(iso), 'dd/MM/yy HH:mm', { locale: ptBR });
}

// ============================================================================
// LINHA DA TABELA INTERNA
// ============================================================================

function PayoutRow({
  detail,
  onOpenPayout,
}: {
  detail: CommissionPayoutDetail;
  onOpenPayout?: (payoutId: string) => void;
}) {
  const statusInfo =
    PAYOUT_STATUS_CONFIG[detail.payout_status] ?? PAYOUT_STATUS_CONFIG.pending;

  const payoutClickable = !!onOpenPayout && !!detail.payout_id;

  return (
    <TableRow className="hover:bg-muted/40 border-b border-border/40 group transition-colors">
      {/* Barra lateral por status do pagamento */}
      <TableCell className="p-0 w-2">
        <StatusBar colorClass={getPayoutStatusBarColor(detail.payout_status)} />
      </TableCell>

      {/* Ref. Pagamento — UUID 8 chars uppercase, clicável */}
      <TableCell className="px-2 py-1.5">
        <button
          type="button"
          onClick={() => {
            if (payoutClickable) onOpenPayout!(detail.payout_id);
          }}
          disabled={!payoutClickable}
          className={cn(
            'inline-flex items-center gap-1 font-mono text-[11px]',
            payoutClickable
              ? 'text-primary hover:underline cursor-pointer'
              : 'text-muted-foreground cursor-default',
          )}
          title={payoutClickable ? 'Abrir detalhes do pagamento' : undefined}
        >
          {detail.payout_id.substring(0, 8).toUpperCase()}
          {payoutClickable && (
            <ExternalLink className="h-2.5 w-2.5 opacity-60" />
          )}
        </button>
      </TableCell>

      {/* Status do pagamento */}
      <TableCell className="px-2 py-1.5">
        <Badge
          variant="outline"
          className={cn('text-[10px] h-5 shrink-0', statusInfo.color)}
        >
          {statusInfo.label}
        </Badge>
      </TableCell>

      {/* Método */}
      <TableCell className="px-2 py-1.5 text-xs text-muted-foreground">
        {detail.payout_method ?? '—'}
      </TableCell>

      {/* Valor do link */}
      <TableCell className="text-right px-2 py-1.5">
        <span className="text-sm font-medium tabular-nums">
          {formatBRL(detail.link_reward_value)}
        </span>
      </TableCell>

      {/* Vinculado em */}
      <TableCell className="px-2 py-1.5 text-xs text-muted-foreground">
        {formatDate(detail.linked_at)}
      </TableCell>

      {/* Estado do vínculo: Ativo (verde) ou Liberado (cinza) */}
      <TableCell className="px-2 py-1.5">
        {detail.is_active ? (
          <Badge
            variant="outline"
            className="text-[10px] h-5 bg-green-500 text-white border-green-500"
          >
            Ativo
          </Badge>
        ) : (
          <div className="flex flex-col gap-0.5">
            <Badge
              variant="outline"
              className="text-[10px] h-5 bg-muted-foreground/20 text-muted-foreground border-muted-foreground/30 w-fit"
            >
              Liberado
            </Badge>
            <span className="text-[10px] text-muted-foreground tabular-nums">
              {formatDate(detail.released_at)}
            </span>
          </div>
        )}
      </TableCell>

      {/* Motivo de liberação */}
      <TableCell className="px-2 py-1.5 text-xs text-muted-foreground max-w-[180px]">
        <span className="truncate block" title={detail.released_reason ?? undefined}>
          {detail.released_reason ?? (detail.is_active ? '—' : '(sem motivo)')}
        </span>
      </TableCell>
    </TableRow>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

interface CommissionPayoutsModalProps {
  /** ID da comissão — `null` mantém o modal fechado. */
  commissionId: string | null;
  /** Estado controlado de abertura (espelha `!!commissionId`). */
  open: boolean;
  /** Callback de fechamento (limpa o `commissionId` na página). */
  onClose: () => void;
  /** Abre o detalhe do pagamento (`PayoutDetailSheet`). */
  onOpenPayout?: (payoutId: string) => void;
  /** Valor da comissão (exibido no header — opcional, melhora contexto). */
  commissionRewardValue?: number | null;
  /** Status da comissão (exibido como badge no header — opcional). */
  commissionStatusLabel?: string | null;
  /** Cor do badge de status da comissão. */
  commissionStatusColor?: string | null;
}

/**
 * Modal de drill-down: lista todos os pagamentos vinculados a uma comissão.
 */
export function CommissionPayoutsModal({
  commissionId,
  open,
  onClose,
  onOpenPayout,
  commissionRewardValue,
  commissionStatusLabel,
  commissionStatusColor,
}: CommissionPayoutsModalProps) {
  const { data = [], isLoading } = useCommissionPayoutsDetail(
    open ? commissionId : null,
  );

  // Soma client-side dos valores dos links (auditoria visual no footer).
  const total = useMemo(
    () => data.reduce((sum, p) => sum + Number(p.link_reward_value ?? 0), 0),
    [data],
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      {/* Layout "wizard premium" do projeto: max-w-5xl + 85dvh + p-0,
          fullscreen no mobile. */}
      <DialogContent className="max-w-5xl w-[95vw] max-h-[85dvh] p-0 gap-0 flex flex-col">
        {/* ====================== HEADER ====================== */}
        <DialogHeader className="px-5 py-4 border-b border-border/60 space-y-1.5 shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base font-semibold">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Wallet className="h-3.5 w-3.5" />
            </span>
            Pagamentos da comissão
            {commissionId && (
              <span className="font-mono text-xs text-muted-foreground uppercase">
                #{commissionId.substring(0, 8).toUpperCase()}
              </span>
            )}
          </DialogTitle>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {typeof commissionRewardValue === 'number' && (
              <>
                <span className="font-medium tabular-nums text-foreground">
                  {formatBRL(commissionRewardValue)}
                </span>
                {commissionStatusLabel && <span>•</span>}
              </>
            )}
            {commissionStatusLabel && (
              <Badge
                variant="outline"
                className={cn(
                  'text-[10px] h-5',
                  commissionStatusColor ??
                    'bg-muted text-foreground border-muted',
                )}
              >
                {commissionStatusLabel}
              </Badge>
            )}
          </div>
        </DialogHeader>

        {/* ====================== CORPO ====================== */}
        <div className="flex-1 min-h-0 overflow-auto">
          {isLoading ? (
            <div className="space-y-2 p-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : data.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
              <div className="h-12 w-12 rounded-full bg-muted/50 flex items-center justify-center">
                <HandCoins className="h-6 w-6 text-muted-foreground" />
              </div>
              <div className="text-center">
                <h3 className="font-medium text-sm">Nenhum pagamento vinculado</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Esta comissão ainda não foi incluída em nenhum pagamento.
                </p>
              </div>
            </div>
          ) : (
            <table
              className="w-max caption-bottom text-sm table-fixed"
              style={{ minWidth: '900px' }}
            >
              <colgroup>
                <col style={{ width: 8 }} />   {/* Barra de status */}
                <col className="w-[110px]" />  {/* Ref. Pagamento */}
                <col className="w-[110px]" />  {/* Status */}
                <col className="w-[110px]" />  {/* Método */}
                <col className="w-[120px]" />  {/* Valor do link */}
                <col className="w-[140px]" />  {/* Vinculado em */}
                <col className="w-[120px]" />  {/* Estado vínculo */}
                <col className="w-[180px]" />  {/* Motivo liberação */}
              </colgroup>

              <TableHeader className="sticky top-0 bg-background z-[1]">
                <TableRow className="bg-muted/20 hover:bg-muted/20 border-b border-border/40">
                  <TableHead className="w-2 p-0" />
                  <TableHead className="text-xs font-medium px-2 py-2">Ref. Pagamento</TableHead>
                  <TableHead className="text-xs font-medium px-2 py-1.5">Status</TableHead>
                  <TableHead className="text-xs font-medium px-2 py-1.5">Método</TableHead>
                  <TableHead className="text-xs font-medium text-right px-2 py-1.5">Valor</TableHead>
                  <TableHead className="text-xs font-medium px-2 py-1.5">Vinculado em</TableHead>
                  <TableHead className="text-xs font-medium px-2 py-1.5">Vínculo</TableHead>
                  <TableHead className="text-xs font-medium px-2 py-1.5">Motivo liberação</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {data.map((d) => (
                  <PayoutRow
                    key={d.link_id}
                    detail={d}
                    onOpenPayout={onOpenPayout}
                  />
                ))}
              </TableBody>
            </table>
          )}
        </div>

        {/* ====================== FOOTER ====================== */}
        <div className="px-5 py-3 border-t border-border/60 flex items-center justify-between gap-3 shrink-0 bg-muted/10">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <CircleDollarSign className="h-3.5 w-3.5" />
            <span>
              <span className="font-medium text-foreground tabular-nums">
                {data.length}
              </span>{' '}
              {data.length === 1 ? 'pagamento' : 'pagamentos'} • Soma vinculada:{' '}
              <span className="font-medium text-foreground tabular-nums">
                {formatBRL(total)}
              </span>
            </span>
          </div>
          <Button variant="outline" size="sm" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
